import React, { FormEvent } from "react";
import { Button, Fab } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { FullScreenDialog } from "./FullScreenDialog";

import { Formik } from "formik";
import { addNewSubscription } from "../services/api/subscription.api";
import { getBillingPlans, BillingPlan } from "../services/api/billingPlans.api";
import { getUniqueCustomerKeyCompanyId } from "../services/api/subscription.api";
import { SubscriptionCustomerCompanyKey } from "../models/Subscription";
import { getCompanies, Company } from "../services/api/companies.api";
import { getFeeTypeLabels, FeeTypeLabel } from "../services/api/feeTypeLabels.api";
import { getIdentityProviders, IdentityProvider } from "../services/api/identityProviders.api";
import { getLicenseTypes, LicenseType } from "../services/api/licenseTypes.api";
import {
	getProductTierBillingPlans,
	getProductTiers,
	ProductTierXBillingPlan,
	ProductTier,
} from "../services/api/productTiers.api";
import { getProducToS, ToS } from "../services/api/termsOfService.api";

import {
	AddNewSubscriptionRequestInitialValue,
	AddNewSubscriptionRequestContactInitialValue,
	AddNewSubscriptionRequestValidationSchema,
	AddNewSubscriptionRequest,
} from "../models/Requests/AddNewSubscriptionRequest";
import { CURRENCY, PERIOD } from "../constants";
import { useSnackbar } from "notistack";

import { FormikTextInputField } from "./Form/FormikTextInput";
import { FormSection } from "./Form/FormSection";
import { FormikSwitchInputField } from "./Form/FormikSwitchInput";
import { showSnack } from "../utils/component";

interface AddNewSubscriptionProps {
	// id of the subscription product
	productId: string;
	// function to call on completion
	done: () => void;
	// initial values
	initialValues: AddNewSubscriptionRequest;
}

const styles = {
	wrapper: {
		display: "flex",
		flexDirection: "column" as any,
		alignItems: "center",
	},
	deleteContactButton: {
		marginLeft: 24,
	},
};

export const AddNewSubscription = (props: AddNewSubscriptionProps) => {
	const [companies, setCompanies] = React.useState<Company[]>([]);
	const [identityProviders, setIdentityProviders] = React.useState<IdentityProvider[]>([]);
	const [billingPlans, setBillingPlans] = React.useState<BillingPlan[]>([]);
	const [subscriptionCustomerCompanyKeys, setSubscriptionCustomerCompanyKeys] = React.useState<
		SubscriptionCustomerCompanyKey[]
	>([]);
	const [feeTypeLabels, setFeeTypeLabels] = React.useState<FeeTypeLabel[]>([]);
	const [licenseTypes, setLicenseTypes] = React.useState<LicenseType[]>([]);
	const [productTiers, setProductTiers] = React.useState<ProductTier[]>([]);
	const [productTierBillingPlans, setProductTierBillingPlans] = React.useState<ProductTierXBillingPlan[]>([]);
	const [termsOfService, setTermsOfService] = React.useState<ToS | null>();

	const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);

	const [isAddFormOpen, setIsAddFormOpen] = React.useState(false);

	const { enqueueSnackbar } = useSnackbar();

	React.useEffect(() => {
		const getData = async () => {
			const companiesResponse = getCompanies()();
			const customersResponse = getUniqueCustomerKeyCompanyId(props.productId)();
			const idProvidersResponse = getIdentityProviders()();
			const billingPlansResponse = getBillingPlans()();
			const feeTypeLabelsResponse = getFeeTypeLabels()();
			const licenseTypesResponse = getLicenseTypes()();
			const productTiersResponse = getProductTiers(props.productId)();
			const termsOfServiceResponse = getProducToS(props.productId)();
			const productTierBillingPlansResponse = getProductTierBillingPlans(props.productId)();

			setCompanies((await companiesResponse).data);
			setSubscriptionCustomerCompanyKeys((await customersResponse).data);
			setIdentityProviders((await idProvidersResponse).data);
			setBillingPlans((await billingPlansResponse).data);
			setFeeTypeLabels((await feeTypeLabelsResponse).data);
			setLicenseTypes((await licenseTypesResponse).data);
			setProductTiers((await productTiersResponse).data);
			setTermsOfService((await termsOfServiceResponse).data);
			setProductTierBillingPlans((await productTierBillingPlansResponse).data);
		};

		if (isAddFormOpen) {
			getData();
		}
	}, [isAddFormOpen, props.productId]);

	let submitForm: (e: FormEvent<HTMLFormElement> | undefined) => void;

	const getMatchingProductTierBillingPlan = (productTierId: string, billingPlanId: string) => {
		const matches = productTierBillingPlans.filter(
			(x) => x.billingPlanId === billingPlanId && x.productTierId === productTierId,
		);

		return matches.length > 0 ? matches[0] : null;
	};

	const actions = (
		<div>
			<Button
				data-testid="add-new-subscription-save-btn"
				variant="contained"
				disabled={isFormSubmitting}
				color="primary"
				onClick={() => {
					if (submitForm) {
						(submitForm as any)();
					}
				}}
			>
				Save changes
			</Button>
		</div>
	);

	const submitAddNewSubscriptionForm = async (values: AddNewSubscriptionRequest) => {
		try {
			setIsFormSubmitting(true);

			await addNewSubscription(props.productId, values)();

			showSnack(enqueueSnackbar, "Changes saved successfully");

			setIsAddFormOpen(false);

			props.done();
		} catch {
			showSnack(enqueueSnackbar, "There was an error while saving changes", "error");
		}

		setIsFormSubmitting(false);
	};

	return (
		<FullScreenDialog
			label="Add New Subscription"
			title="Add New Subscription"
			isOpen={isAddFormOpen}
			emitState={(isOpen) => setIsAddFormOpen(isOpen)}
			actions={actions}
		>
			<div data-testid="add-new-subscription-form">
				<Formik
					initialValues={props.initialValues}
					validationSchema={AddNewSubscriptionRequestValidationSchema(
						billingPlans.map((x) => x.billingPlanName),
						companies.map((x) => x.companyName),
						companies.map((x) => x.uniqueCompanyIdentifier),
						subscriptionCustomerCompanyKeys,
					)}
					onSubmit={submitAddNewSubscriptionForm}
				>
					{({ handleSubmit, setFieldValue, values }) => {
						submitForm = handleSubmit;

						return (
							<div style={styles.wrapper}>
								<FormSection title="General Information" data-testid="add-new-subscription-general-section">
									<FormikTextInputField name="customerKey" label="Customer key" optimized={true} />
									<FormikTextInputField
										name="customerKeyDescription"
										label="Customer key description"
										optimized={true}
									/>
									<FormikSwitchInputField name="autoRenew" label="Auto renew?" optimized={true} />
								</FormSection>

								<FormSection
									title="Contacts"
									data-testid="add-new-subscription-contacts-section"
									headerActions={
										<Button
											color="secondary"
											onClick={() =>
												setFieldValue("contacts", [...values.contacts, AddNewSubscriptionRequestContactInitialValue])
											}
										>
											Add contact
										</Button>
									}
								>
									{values.contacts.map((_contact, i) => (
										<div key={i} style={{ marginBottom: "24px" }}>
											<FormikTextInputField name={`contacts.${i}.firstName`} label="First name" optimized={true} />
											<FormikTextInputField name={`contacts.${i}.lastName`} label="Last name" optimized={true} />
											<FormikTextInputField name={`contacts.${i}.email`} label="Email" optimized={true} />
											<FormikTextInputField
												name={`contacts.${i}.phoneNumberCountryCode`}
												label="Phone number country code"
												optimized={true}
											/>
											<FormikTextInputField name={`contacts.${i}.phoneNumber`} label="Phone number" optimized={true} />

											{i > 0 && (
												<Fab
													color="secondary"
													size="small"
													aria-label="delete"
													style={styles.deleteContactButton}
													onClick={() => {
														setFieldValue("contacts", [
															...values.contacts.slice(0, i),
															...values.contacts.slice(i + 1),
														]);
													}}
												>
													<DeleteIcon />
												</Fab>
											)}
										</div>
									))}
								</FormSection>

								<FormSection
									title="Company"
									data-testid="add-new-subscription-company-section"
									headerActions={
										<FormikSwitchInputField
											name="isNewCompany"
											label="New company?"
											optimized={true}
											changeHandler={(e: React.ChangeEvent<any>) => {
												if (e.target.checked) {
													setFieldValue("companyId", AddNewSubscriptionRequestInitialValue.companyId, false);
												} else {
													setFieldValue("company", AddNewSubscriptionRequestInitialValue.company, false);
												}
											}}
										/>
									}
								>
									<>
										{values.isNewCompany && (
											<>
												<FormikTextInputField name="company.companyName" label="Company name" optimized={true} />

												<FormikTextInputField
													name="company.identityProviderId"
													label="Identity providerId"
													optimized={false}
													isSelect={true}
													selectOptions={identityProviders.map((x) => ({
														val: x.identityProviderId,
														text: x.identityProviderName,
													}))}
													appendEmptySelectOption={true}
												/>

												<FormikTextInputField
													name="company.uniqueCompanyIdentifier"
													label="Unique company identifier"
													optimized={true}
												/>
											</>
										)}
										{!values.isNewCompany && (
											<FormikTextInputField
												name="companyId"
												label="Company"
												optimized={false}
												isSelect={true}
												selectOptions={companies.map((x) => ({
													val: x.companyId,
													text: x.companyName,
												}))}
												appendEmptySelectOption={true}
											/>
										)}
									</>
								</FormSection>

								<FormSection
									title="Billing Plan"
									data-testid="add-new-subscription-billing-plan-section"
									headerActions={
										<FormikSwitchInputField
											name="isNewBillingPlan"
											label="New billing plan?"
											optimized={true}
											changeHandler={(e: React.ChangeEvent<any>) => {
												if (e.target.checked) {
													setFieldValue("billingPlanId", AddNewSubscriptionRequestInitialValue.billingPlanId);
													setFieldValue(
														"productTierXBillingPlan.billingPlanId" as any,
														AddNewSubscriptionRequestInitialValue.productTierXBillingPlan.billingPlanId,
													);
												} else {
													setFieldValue("billingPlan", AddNewSubscriptionRequestInitialValue.billingPlan);
													setFieldValue(
														"productTierXBillingPlan.invoiceDescription" as any,
														AddNewSubscriptionRequestInitialValue.productTierXBillingPlan.invoiceDescription,
													);
												}
											}}
										/>
									}
								>
									<>
										{values.isNewBillingPlan && (
											<>
												<FormikTextInputField
													name="billingPlan.billingPlanName"
													label="Billing plan name"
													optimized={true}
												/>

												<FormikTextInputField name="billingPlan.costFixed" label="Fixed cost" optimized={true} />

												<FormikTextInputField name="billingPlan.costResource" label="Resource cost" optimized={true} />

												<FormikTextInputField name="billingPlan.costUsage" label="Usage cost" optimized={true} />

												<FormikTextInputField
													name="billingPlan.currency"
													label="Currency"
													optimized={false}
													isSelect={true}
													selectOptions={CURRENCY}
													appendEmptySelectOption={true}
												/>

												<FormikTextInputField
													name="billingPlan.licenseTypeId"
													label="License type"
													optimized={false}
													isSelect={true}
													selectOptions={licenseTypes.map((x) => ({
														val: x.licenseTypeId,
														text: x.licenseTypeName,
													}))}
													appendEmptySelectOption={true}
												/>

												<FormikSwitchInputField
													name="billingPlan.defaultAutoRenew"
													label="Default auto renew?"
													optimized={true}
												/>

												<FormikSwitchInputField
													name="billingPlan.invoiceAtStartOfPeriod"
													label="Invoice at start of period?"
													optimized={true}
												/>

												<FormikSwitchInputField name="billingPlan.isCustom" label="Is custom?" optimized={true} />

												<FormikSwitchInputField
													name="billingPlan.isEnabledForPurchase"
													label="Is enabled for purchase?"
													optimized={true}
												/>

												<FormikTextInputField
													name="billingPlan.period"
													label="Period"
													optimized={false}
													isSelect={true}
													selectOptions={PERIOD}
													appendEmptySelectOption={true}
												/>

												<FormikTextInputField
													name="billingPlan.resourceCountMinimum"
													label="Resource count minimum"
													optimized={true}
												/>
												<FormikTextInputField
													name="billingPlan.resourceCountMaximum"
													label="Resource count maximum"
													optimized={true}
												/>

												<FormikTextInputField
													name="billingPlan.usageMetricMinimum"
													label="Usage metric minimum"
													optimized={true}
												/>
												<FormikTextInputField
													name="billingPlan.usageMetricMaximum"
													label="Usage metric maximum"
													optimized={true}
												/>

												<FormikTextInputField
													name="billingPlan.paymentTermInDays"
													label="Payment term in days"
													optimized={true}
												/>

												<FormikTextInputField
													name="billingPlan.fixedFeeTypeLabelId"
													label="Fixed fee type label"
													optimized={false}
													isSelect={true}
													selectOptions={feeTypeLabels.map((x) => ({
														val: x.feeTypeLabelId,
														text: x.description,
													}))}
													appendEmptySelectOption={true}
												/>

												<FormikTextInputField
													name="billingPlan.resourceFeeTypeLabelId"
													label="Resource fee type label"
													optimized={false}
													isSelect={true}
													selectOptions={feeTypeLabels.map((x) => ({
														val: x.feeTypeLabelId,
														text: x.description,
													}))}
													appendEmptySelectOption={true}
												/>

												<FormikTextInputField
													name="billingPlan.usageFeeTypeLabelId"
													label="Usage fee type label"
													optimized={false}
													isSelect={true}
													selectOptions={feeTypeLabels.map((x) => ({
														val: x.feeTypeLabelId,
														text: x.description,
													}))}
													appendEmptySelectOption={true}
												/>
											</>
										)}
										{!values.isNewBillingPlan && (
											<FormikTextInputField
												name="billingPlanId"
												label="Billing plan"
												optimized={false}
												isSelect={true}
												selectOptions={billingPlans.map((x) => ({
													val: x.billingPlanId,
													text: x.billingPlanName,
												}))}
												appendEmptySelectOption={true}
												changeHandler={(e: React.ChangeEvent<any>) => {
													const { productTierXBillingPlanId, invoiceDescription } =
														getMatchingProductTierBillingPlan(
															values.productTierXBillingPlan.productTierId,
															e.target.value,
														) || {};

													setFieldValue("productTierXBillingPlan.billingPlanId" as any, e.target.value);
													setFieldValue("productTierXBillingPlanId" as any, productTierXBillingPlanId);
													setFieldValue("productTierXBillingPlan.invoiceDescription" as any, invoiceDescription);
												}}
											/>
										)}

										<FormikTextInputField
											name="productTierXBillingPlan.productTierId"
											label="Product tier"
											optimized={false}
											isSelect={true}
											selectOptions={productTiers.map((x) => ({
												val: x.productTierId,
												text: x.productTierName,
											}))}
											appendEmptySelectOption={true}
											changeHandler={(e: React.ChangeEvent<any>) => {
												const { productTierXBillingPlanId, invoiceDescription } =
													getMatchingProductTierBillingPlan(e.target.value, values.billingPlanId) || {};

												setFieldValue("productTierXBillingPlan.billingPlanId" as any, values.billingPlanId);
												setFieldValue("productTierXBillingPlanId", productTierXBillingPlanId);
												setFieldValue("productTierXBillingPlan.invoiceDescription" as any, invoiceDescription);
											}}
										/>

										<FormikTextInputField
											name="productTierXBillingPlan.invoiceDescription"
											label="Invoice description"
											disabled={!!values.productTierXBillingPlanId}
											optimized={true}
										/>
									</>
								</FormSection>

								<FormSection title="Terms of Service" data-testid="add-new-subscription-tos-section">
									<FormikTextInputField name="termsOfServiceAcceptance.acceptor" label="Acceptor" optimized={true} />

									<FormikTextInputField
										name="termsOfServiceAcceptance.dateAccepted"
										label="Date accepted"
										optimized={true}
										isDate={true}
									/>

									<FormikTextInputField
										name="termsOfServiceAcceptance.termsOfServiceId"
										label="Terms of service"
										optimized={false}
										isSelect={true}
										selectOptions={
											termsOfService ? [{ val: termsOfService.termsOfServiceId, text: termsOfService.fileBlobURI }] : []
										}
										appendEmptySelectOption={true}
									/>
								</FormSection>
							</div>
						);
					}}
				</Formik>
			</div>
		</FullScreenDialog>
	);
};
